import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, getStoredUser, setStoredUser } from "../lib/chalkApi";

const AuthCtx = createContext(null);

/**
 * Auth state holder.
 *
 * Transport: the JWT lives in a Secure;HttpOnly;SameSite=Lax cookie set by
 * the backend on signin/signup/magic-consume/join. JS never touches it.
 * The browser includes the cookie automatically on every API call because
 * the axios client is configured with `withCredentials: true`.
 *
 * To know whether the user is currently authenticated, we call `/auth/me`
 * on mount — a 200 means the cookie was valid, a 401 means it's missing
 * or expired. We cache the user record itself in localStorage purely so
 * the UI can render an instant "remembered" user state on reload; that
 * cached record is non-sensitive and is verified against `/me` before any
 * mutation.
 */
export function AuthProvider({ children }) {
  const cached = getStoredUser();
  const [user, setUser] = useState(cached);
  // Always re-verify the cookie on boot, even when we have a cached user.
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.me();
        if (cancelled) return;
        setUser(data.user);
        setStoredUser(data.user);
      } catch {
        if (cancelled) return;
        // Cookie missing/expired — drop any cached user record.
        setStoredUser(null);
        setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const signin = useCallback(async (email, password) => {
    const data = await api.signin(email, password);
    // The cookie is already set by the response; we just cache the user.
    setStoredUser(data.user);
    setUser(data.user);
    return data.user;
  }, []);

  const refresh = useCallback(async () => {
    // Re-fetch /auth/me after an external auth event (e.g. the demo endpoint
    // set the cookie out-of-band). Mirrors the boot path.
    try {
      const data = await api.me();
      setStoredUser(data.user);
      setUser(data.user);
      return data.user;
    } catch {
      setStoredUser(null);
      setUser(null);
      return null;
    }
  }, []);

  const signup = useCallback(async (payload) => {
    const data = await api.signup(payload);
    setStoredUser(data.user);
    setUser(data.user);
    return data.user;
  }, []);

  const consumeMagic = useCallback(async (token) => {
    const data = await api.magicConsume(token);
    setStoredUser(data.user);
    setUser(data.user);
    return data.user;
  }, []);

  const acceptInvite = useCallback(async (payload) => {
    const data = await api.join(payload);
    setStoredUser(data.user);
    setUser(data.user);
    return data.user;
  }, []);

  const signout = useCallback(async () => {
    // Tell the server to clear the cookie. We don't block the UI on errors;
    // worst case the cookie expires naturally.
    try { await api.signout(); } catch { /* ignore network */ }
    setStoredUser(null);
    setUser(null);
  }, []);

  const switchRole = useCallback(async (role) => {
    // Switch the user's account role (coach ↔ guardian) and refresh both the
    // in-memory user and the stored token. Returns the updated user.
    const data = await api.switchRole(role);
    if (data && data.user) {
      setStoredUser(data.user);
      setUser(data.user);
    }
    return data && data.user;
  }, []);

  const value = useMemo(
    () => ({ user, loading, signin, signup, signout, consumeMagic, acceptInvite, refresh, switchRole }),
    [user, loading, signin, signup, signout, consumeMagic, acceptInvite, refresh, switchRole]
  );

  return (
    <AuthCtx.Provider value={value}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const v = useContext(AuthCtx);
  if (!v) throw new Error("useAuth must be inside AuthProvider");
  return v;
}
